<script setup>
import { computed, useSlots } from 'vue';
import { useI18n } from 'vue-i18n';
import { useGamification } from 'dashboard/composables/lais/useGamification';
import LaisActivityFeed from './LaisActivityFeed.vue';
import LaisGamificationBar from './LaisGamificationBar.vue';
import LaisMissionCard from './LaisMissionCard.vue';

const props = defineProps({
  userId: { type: [Number, String], default: null },
  showRail: { type: Boolean, default: false },
  showBarMission: { type: Boolean, default: true },
  mission: { type: Object, default: null },
});

const emit = defineEmits([
  'rules',
  'rewards',
  'mission',
  'openActivity',
  'react',
]);
const { t } = useI18n();
const slots = useSlots();
const gamification = useGamification({ userId: () => props.userId });
const { activities, achievements, enabled, loading, profile } = gamification;
const mission = computed(() => props.mission || achievements.value[0] || null);
const hasDefaultContent = computed(() => Boolean(slots.default?.().length));
const hasRail = computed(
  () => props.showRail && enabled.value && hasDefaultContent.value
);
</script>

<template>
  <div class="flex min-h-full min-w-0 flex-col bg-[#f8f8fc]">
    <div
      v-if="hasRail"
      class="grid min-h-full min-w-0 xl:grid-cols-[minmax(0,1fr)_360px]"
    >
      <section class="min-w-0">
        <div class="w-full px-4 pt-4 sm:px-6 lg:px-4 xl:px-5">
          <LaisGamificationBar
            data-testid="lais-gamification-bar"
            :profile="profile"
            :mission="mission"
            :loading="loading"
            :show-mission="showBarMission"
            @rules="emit('rules')"
            @rewards="emit('rewards')"
            @mission="emit('mission')"
          />
        </div>
        <div class="min-w-0">
          <slot :gamification="gamification" />
        </div>
      </section>

      <aside
        data-testid="lais-gamification-rail"
        class="min-w-0 border-l border-lais-border-soft bg-white px-4 py-4 xl:min-h-screen"
      >
        <slot name="rail" :gamification="gamification">
          <div class="space-y-4">
            <LaisMissionCard
              v-if="mission"
              :title="mission.title || t('GAMIFICATION.BAR.DAILY_MISSION')"
              :description="mission.description"
              :points="mission.points || mission.threshold"
              :progress="mission.progress"
              :target="mission.target || mission.threshold"
              :completed="mission.active === false"
              @action="emit('mission', mission)"
            />
            <LaisActivityFeed
              :activities="activities"
              :loading="loading"
              @open="emit('openActivity')"
              @react="emit('react', $event)"
            />
          </div>
        </slot>
      </aside>
    </div>

    <template v-else>
      <div v-if="enabled" class="w-full px-4 pt-4 sm:px-6 lg:px-5">
        <LaisGamificationBar
          data-testid="lais-gamification-bar"
          :profile="profile"
          :mission="mission"
          :loading="loading"
          :show-mission="showBarMission"
          @rules="emit('rules')"
          @rewards="emit('rewards')"
          @mission="emit('mission')"
        />
      </div>
      <slot :gamification="gamification" />
    </template>
  </div>
</template>
